import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import {
  CalendarClock, MapPin, Video, ArrowLeft, Loader2, CalendarPlus, Pencil, FileText,
  Check, X, HelpCircle, Clock, User, ExternalLink, Gavel, Send, Sparkles,
} from 'lucide-react';
import AccessGate from '../components/AccessGate';
import MeetingForm from '../components/MeetingForm';
import MeetingVoting, { ApplicationBadge } from '../components/MeetingVoting';
import { useAuth } from '../context/AuthContext';
import ActionFormModal from '../components/ActionFormModal';
import { AgendaSuggestions } from '../components/AgendaSuggestions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmt = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' }); } catch { return dt; } };
const fmtDate = (dt) => { if (!dt) return ''; try { return new Date(dt).toLocaleDateString([], { dateStyle: 'medium' }); } catch { return dt; } };

const RSVP_OPTS = [
  { v: 'going', label: 'Going', icon: Check, color: '#1c6b2c' },
  { v: 'maybe', label: 'Maybe', icon: HelpCircle, color: '#9a6b00' },
  { v: 'not_going', label: "Can't make it", icon: X, color: '#8a1f11' },
];
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', done: 'Done' };
const STATUS_COLOR = { open: '#8a1f11', in_progress: '#9a6b00', done: '#1c6b2c' };

const outcome = (t) => {
  if (!t) return { label: 'Result pending', color: 'var(--olive)' };
  if (t.yes > t.no) return { label: 'Carried', color: '#1c6b2c' };
  if (t.no > t.yes) return { label: 'Not carried', color: '#8a1f11' };
  return { label: 'Tied', color: '#9a6b00' };
};

function Decisions({ motions, canManage }) {
  const decided = (motions || []).filter((mo) => mo.status === 'closed' && mo.tally);
  if (!decided.length) return null;
  return (
    <div className="mt-4 rounded-lg border p-4" style={{ borderColor: 'var(--gold)', background: 'rgba(184,146,58,0.08)' }} data-testid="detail-decisions">
      <h2 className="font-title text-base flex items-center gap-2 mb-2" style={{ color: 'var(--maroon)' }}><Gavel size={17} /> Decisions</h2>
      <ul className="space-y-2">
        {decided.map((mo) => {
          const o = outcome(mo.tally);
          return (
            <li key={mo.id} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`decision-${mo.id}`}>
              <span className="text-sm min-w-0 flex items-center gap-2 flex-wrap" style={{ color: 'var(--ink)' }}>{mo.question}<ApplicationBadge motion={mo} canManage={canManage} /></span>
              <span className="flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-full font-title" style={{ background: 'white', color: o.color, border: `1px solid ${o.color}` }}>{o.label}</span>
                <span style={{ color: 'var(--olive)' }}>Y {mo.tally.yes} · N {mo.tally.no} · A {mo.tally.abstain}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function MeetingDetail() {
  const { id } = useParams();
  const [newAction, setNewAction] = useState(false);
  const { getMeeting, rsvpMeeting, setActionStatus, meetingIcsUrl, adminMarkAttendance, user, meetingAgendaPdfUrl, meetingMinutesPdfUrl, emailMeetingMinutes, aiMinutesProse, saveMeetingMinutes } = useAuth();
  const [emailingMinutes, setEmailingMinutes] = useState(false);
  const [prose, setProse] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [m, setM] = useState(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    getMeeting(id).then(setM).catch((e) => setErr(e?.response?.data?.detail || 'Could not load this meeting.'));
  }, [getMeeting, id]);
  useEffect(load, [load]);

  const doRsvp = async (v) => { try { setM(await rsvpMeeting(id, v)); } catch (e) { toast.error('Could not save your RSVP.'); } };
  const emailMinutes = async () => {
    if (!window.confirm('Generate the minutes and email them (with a PDF attached) to all attendees?')) return;
    setEmailingMinutes(true);
    try {
      const r = await emailMeetingMinutes(id);
      toast.success(r.mocked ? `Minutes prepared for ${r.recipients} attendee(s) (email is in preview/mock mode).` : `Minutes emailed to ${r.sent} of ${r.recipients} attendee(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not email the minutes.'); }
    finally { setEmailingMinutes(false); }
  };
  const aiDraft = async () => {
    setAiBusy(true);
    try { const r = await aiMinutesProse(id); setProse(r.html); }
    catch (e) { toast.error(e?.response?.data?.detail || 'The minutes could not be drafted.'); }
    finally { setAiBusy(false); }
  };
  const changeAction = async (aid, status) => {
    try { setM(await setActionStatus(id, aid, status)); toast.success('Action updated.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the action.'); }
  };
  const toggleAttendance = async (email, present) => {
    setM((cur) => {
      const rest = (cur.attendance || []).filter((a) => a.email !== email);
      return { ...cur, attendance: [...rest, { email, present }] };
    });
    try { setM(await adminMarkAttendance(id, email, present)); } catch (e) { toast.error('Could not update attendance.'); load(); }
  };

  if (err) return <div className="max-w-3xl mx-auto px-4 py-16 text-center"><p className="font-title text-xl" style={{ color: 'var(--maroon)' }}>{err}</p><Link to="/meetings" className="text-sm hover:underline mt-3 inline-block" style={{ color: 'var(--navy)' }}>← Back to meetings</Link></div>;
  if (!m) return <div className="flex items-center justify-center py-32 gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={18} /> Loading meeting…</div>;

  const attendance = Object.fromEntries((m.attendance || []).map((a) => [a.email, a.present]));
  const rsvpByEmail = Object.fromEntries((m.rsvps || []).map((r) => [r.email, r.status]));
  const myEmail = (user?.email || '').toLowerCase();

  return (
    <AccessGate pageKey="meetings">
      <div className="max-w-3xl mx-auto px-4 py-8 fade-in" data-testid="meeting-detail">
        <Link to="/meetings" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: 'var(--navy)' }}><ArrowLeft size={15} /> All meetings</Link>

        <div className="flex items-start justify-between gap-3 mt-4 flex-wrap">
          <div>
            <h1 className="font-title text-3xl" style={{ color: 'var(--maroon)' }}>{m.title}</h1>
            <div className="flex flex-wrap gap-4 text-sm mt-2" style={{ color: 'var(--olive)' }}>
              {m.starts_at && <span className="flex items-center gap-1"><CalendarClock size={15} /> {fmt(m.starts_at)}</span>}
              {m.location && <span className="flex items-center gap-1"><MapPin size={15} /> {m.location}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <a href={meetingIcsUrl(m.id)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="detail-ics"><CalendarPlus size={15} /> Add to calendar</a>
            {m.gcal_html_link && <a href={m.gcal_html_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--sage)', color: '#1c6b2c' }} data-testid="detail-gcal"><ExternalLink size={15} /> Google Calendar</a>}
            {m.can_manage && <Link to={`/admin/meetings/${m.id}/conduct`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: '#1c6b2c', color: 'white' }} data-testid="detail-conduct"><Gavel size={15} /> Conduct meeting</Link>}
            {m.can_manage && <a href={meetingAgendaPdfUrl(m.id)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="detail-agenda-pdf"><FileText size={15} /> Agenda PDF</a>}
            {m.can_manage && <a href={meetingMinutesPdfUrl(m.id)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="detail-minutes-pdf"><Gavel size={15} /> Minutes PDF</a>}
            {m.can_manage && <button onClick={emailMinutes} disabled={emailingMinutes} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--olive)', color: 'var(--cream)', opacity: emailingMinutes ? 0.6 : 1 }} data-testid="detail-email-minutes">{emailingMinutes ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Email minutes</button>}
            {m.can_manage && <button onClick={aiDraft} disabled={aiBusy} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--gold)', color: 'var(--ink)', opacity: aiBusy ? 0.6 : 1 }} data-testid="detail-ai-minutes">{aiBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Draft</button>}
            {m.can_manage && <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="detail-edit"><Pencil size={15} /> Edit</button>}
          </div>
        </div>

        {m.meeting_link && (
          <a href={m.meeting_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded font-title text-sm" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="detail-join">
            <Video size={16} /> Join online meeting <ExternalLink size={14} />
          </a>
        )}

        {m.description && <p className="text-[15px] mt-4" style={{ color: 'var(--ink)' }}>{m.description}</p>}

        <Decisions motions={m.motions} canManage={!!m.can_manage} />

        {/* Your RSVP */}
        <div className="mt-6 bg-white rounded-lg border p-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <h2 className="font-title text-base mb-2" style={{ color: 'var(--navy)' }}>Will you attend?</h2>
          <div className="inline-flex rounded-md overflow-hidden" style={{ border: '1px solid rgba(0,0,0,0.12)' }} data-testid="detail-rsvp">
            {RSVP_OPTS.map((o) => { const Icon = o.icon; const active = m.my_rsvp === o.v; return (
              <button key={o.v} onClick={() => doRsvp(o.v)} data-testid={`detail-rsvp-${o.v}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-title border-r last:border-r-0" style={{ borderColor: 'rgba(0,0,0,0.12)', background: active ? o.color : 'white', color: active ? 'white' : 'var(--olive)' }}><Icon size={14} /> {o.label}</button>
            ); })}
          </div>
          <p className="text-xs mt-2" style={{ color: 'var(--olive)' }}>{m.rsvp_summary.going} going · {m.rsvp_summary.maybe} maybe · {m.rsvp_summary.not_going} can't</p>
        </div>

        {/* Agenda */}
        {(m.agenda || []).length > 0 && (
          <section className="mt-6" data-testid="detail-agenda">
            <h2 className="font-title text-xl mb-3" style={{ color: 'var(--maroon)' }}>Agenda</h2>
            <ol className="space-y-2">
              {m.agenda.map((a, i) => (
                <li key={a.id || i} className="bg-white rounded-lg border p-3 flex items-start gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <span className="font-title text-lg" style={{ color: 'var(--gold)' }}>{i + 1}</span>
                  <div className="flex-1">
                    <div className="font-title" style={{ color: 'var(--navy)' }}>{a.title}</div>
                    <div className="flex flex-wrap gap-3 text-xs mt-0.5" style={{ color: 'var(--olive)' }}>
                      {a.presenter && <span className="flex items-center gap-1"><User size={12} /> {a.presenter}</span>}
                      {a.minutes ? <span className="flex items-center gap-1"><Clock size={12} /> {a.minutes} min</span> : null}
                    </div>
                    {a.notes && <p className="text-sm mt-1" style={{ color: 'var(--ink)' }}>{a.notes}</p>}
                  </div>
                </li>
              ))}
            </ol>
          </section>
        )}

        <AgendaSuggestions meeting={m} onUpdated={setM} />

        {/* Documents */}
        {(m.attachments || []).length > 0 && (
          <section className="mt-6" data-testid="detail-docs">
            <h2 className="font-title text-xl mb-3" style={{ color: 'var(--maroon)' }}>Documents</h2>
            <div className="space-y-2">
              {m.attachments.map((a, i) => (
                <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white rounded-lg border p-3 text-sm hover:shadow-sm" style={{ borderColor: 'rgba(0,0,0,0.08)', color: 'var(--navy)' }}>
                  <FileText size={16} style={{ color: 'var(--maroon)' }} /> {a.name || 'Document'} <ExternalLink size={13} className="ml-auto" style={{ color: 'var(--olive)' }} />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Action items */}
        <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="meeting-actions-bar">
          <button onClick={() => setNewAction(true)} className="text-xs px-2.5 py-1.5 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid="meeting-add-action">+ Record an action</button>
          <Link to="/members/actions" className="text-xs underline" style={{ color: 'var(--navy)' }} data-testid="meeting-my-actions-link">My actions</Link>
        </div>
        {newAction && <ActionFormModal source={{ type: 'meeting', id: m.id, title: m.title }} onClose={() => setNewAction(false)} onSaved={() => {}} />}
        {(m.action_items || []).length > 0 && (
          <section className="mt-6" data-testid="detail-actions">
            <h2 className="font-title text-xl mb-3" style={{ color: 'var(--maroon)' }}>Action items</h2>
            <div className="space-y-2">
              {m.action_items.map((a) => {
                const mine = (a.assignee || '').toLowerCase() === myEmail;
                const editable = m.can_manage || mine;
                return (
                  <div key={a.id} className="bg-white rounded-lg border p-3 flex items-center gap-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid={`action-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-title" style={{ color: 'var(--navy)' }}>{a.title}</div>
                      <div className="flex flex-wrap gap-3 text-xs mt-0.5" style={{ color: 'var(--olive)' }}>
                        {a.assignee && <span className="flex items-center gap-1"><User size={12} /> {a.assignee}{mine ? ' (you)' : ''}</span>}
                        {a.due_date && <span className="flex items-center gap-1"><CalendarClock size={12} /> due {fmtDate(a.due_date)}</span>}
                      </div>
                    </div>
                    {editable ? (
                      <Select value={a.status || 'open'} onValueChange={(v) => changeAction(a.id, v)}>
                        <SelectTrigger className="w-36" data-testid={`action-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">Open</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="done">Done</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs px-2 py-1 rounded font-title" style={{ background: 'var(--sage)', color: STATUS_COLOR[a.status] || 'var(--navy)' }}>{STATUS_LABEL[a.status] || a.status}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Motions & anonymous voting */}
        <MeetingVoting meeting={m} onUpdate={setM} />

        {/* Attendees + attendance (managers) */}
        {(m.attendees || []).length > 0 && (
          <section className="mt-6" data-testid="detail-attendees">
            <h2 className="font-title text-xl mb-3" style={{ color: 'var(--maroon)' }}>Attendees</h2>
            <div className="space-y-1.5">
              {m.attendees.map((email) => (
                <div key={email} className="flex items-center gap-3 bg-white rounded-lg border px-3 py-2 text-sm" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <span className="flex-1 truncate" style={{ color: 'var(--navy)' }}>{email}</span>
                  {rsvpByEmail[email] && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--sage)', color: 'var(--olive)' }}>{rsvpByEmail[email].replace('_', ' ')}</span>}
                  {m.can_manage && (
                    <label className="text-xs inline-flex items-center gap-1" style={{ color: 'var(--olive)' }}>
                      <input type="checkbox" checked={!!attendance[email]} onChange={(e) => toggleAttendance(email, e.target.checked)} data-testid={`attendance-${email}`} /> present
                    </label>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
      {editing && <MeetingForm meeting={m} onClose={() => setEditing(false)} onSaved={(saved) => { setM(saved); setEditing(false); }} />}
      {prose !== null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="ai-prose-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Sparkles size={17} /> Drafted minutes</h3>
              <button onClick={() => setProse(null)} data-testid="ai-prose-close"><X size={18} /></button>
            </div>
            <div className="p-5">
              <div className="page-html text-[15px]" style={{ color: 'var(--ink)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(prose) }} />
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => { navigator.clipboard?.writeText(prose); toast('Copied HTML to clipboard'); }} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--navy)' }} data-testid="ai-prose-copy">Copy</button>
              <button onClick={async () => { try { const s = await saveMeetingMinutes(id, prose); setM(s); setProse(null); toast.success('Saved as the official minutes.'); } catch (e) { toast.error('Could not save minutes.'); } }} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--navy)', color: 'var(--cream)' }} data-testid="ai-prose-save">Save as official minutes</button>
              <button onClick={() => setProse(null)} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AccessGate>
  );
}
